import { createContext, Fragment } from "preact";
import { useContext, useEffect, useRef } from "preact/hooks";

export const PopupContext = createContext({
  open: false,
  content: null,
  popups: [],
  // eslint-disable-next-line no-unused-vars
  showPopup: (content) => {},
  hidePopup: () => {},
});

export const usePopup = () => useContext(PopupContext);

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Helper: Get array of focusable children
const getFocusables = (container) =>
  Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

// Helper: Shift initial focus into the modal
const focusInitialElement = (container) => {
  const [firstFocusable] = getFocusables(container);
  if (firstFocusable) {
    firstFocusable.focus();
  } else {
    container.focus();
  }
};

// Helper: Keep tab navigation trapped within modal
const handleTabTrap = (e, container) => {
  if (e.key !== "Tab" || !container) return;

  const focusables = getFocusables(container);
  if (focusables.length === 0) {
    e.preventDefault();
    return;
  }

  const first = focusables[0];
  const last = focusables[focusables.length - 1];
  const current = document.activeElement;

  const isOutside = !container.contains(current);

  if (e.shiftKey && (current === first || current === container || isOutside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (current === last || isOutside)) {
    e.preventDefault();
    first.focus();
  }
};

export const PopupRoot = () => {
  const popup = usePopup();
  const rootRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    if (!popup.open) return;

    // Save active trigger
    previousFocusRef.current = document.activeElement;

    // Shift focus after Preact DOM paint
    const rafId = requestAnimationFrame(() => {
      if (rootRef.current) {
        focusInitialElement(rootRef.current);
      }
    });

    // Capture tab events
    const onKeyDown = (e) => handleTabTrap(e, rootRef.current);
    window.addEventListener("keydown", onKeyDown, true);

    // Cleanup & Restore focus
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("keydown", onKeyDown, true);

      // Defer focus restoration to the next frame so it runs after DOM unmount completes
      const trigger = previousFocusRef.current;
      if (trigger?.focus) {
        requestAnimationFrame(() => {
          trigger.focus();
        });
      }
    };
  }, [popup.open]);

  return popup.open ? (
    <div ref={rootRef} tabIndex={-1} style={{ outline: "none" }}>
      {popup.popups.map((content, i) => (
        <Fragment key={i}>{content}</Fragment>
      ))}
    </div>
  ) : null;
};
